/**
 * Hitches/kits area slugs (all languages). Brand/model segments start at index 2.
 */
const HITCHES_AND_KITS_AREA_SLUGS = new Set(
    [
        'enganches',
        'towbars',
        'attelages',
        'anhangerkupplungen',
        'ganci-di-traino',
        'kits',
        'bausatze',
        'faisceaux',
    ].map(slug => slug.toLowerCase())
);

/**
 * Static hitch/kits path segments (all languages). Must not be uppercased when normalizing URLs.
 */
const HITCHES_AND_KITS_RESERVED_PATH_SEGMENTS = new Set(
    [
        'informes-conformidad',
        'compliance-reports',
        'iframe-search-hitches',
        'tipos-de-bola',
        'towball-types',
        'types-de-rotules',
        'tipi-di-sfera',
        'kugelkopftypen',
        'dcp',
        'accesorios',
        'accessories',
        'accessoires',
        'accesoires',
        'accessori',
        'acessorios',
        'zubehor',
        'recambios',
        'spare-parts',
        'pieces-de-rechange',
        'ricambi',
        'ersatzteile',
        'codificacion',
        'especificos',
        'specifics',
        'specifiques',
        'specifici',
        'spezifische',
    ].map(segment => segment.toLowerCase())
);

function isHitchesOrKitsAreaSlug(segment: string): boolean {
    return HITCHES_AND_KITS_AREA_SLUGS.has(segment.toLowerCase());
}

function isReservedHitchesOrKitsSegment(segment: string): boolean {
    return HITCHES_AND_KITS_RESERVED_PATH_SEGMENTS.has(segment.toLowerCase());
}

function shouldUppercasePathSegment(segment: string): boolean {
    if (!segment || isReservedHitchesOrKitsSegment(segment)) {
        return false;
    }

    return segment !== segment.toUpperCase();
}

/**
 * Normalizes translated URLs before language-switch redirects.
 * - Lowercases language code and fully-uppercase area slugs (e.g. /en/TOWBARS -> /en/towbars).
 * - Preserves mixed-case area slugs (e.g. /de/Verkaufsbedingungen-konditionen).
 * - Uppercases brand/model segments only under hitches/kits areas for EaUppercaseGuard.
 * - Leaves search URLs unchanged beyond language/area slugs (e.g. /en/search/towbars).
 */
export function normalizeTranslatedUrlPath(url: string): string {
    if (!url) {
        return url;
    }

    const queryIndex = url.indexOf('?');
    const hashIndex = url.indexOf('#');

    let pathname = url;
    let suffix = '';

    if (queryIndex !== -1) {
        pathname = url.slice(0, queryIndex);
        suffix = url.slice(queryIndex);
    } else if (hashIndex !== -1) {
        pathname = url.slice(0, hashIndex);
        suffix = url.slice(hashIndex);
    }

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length < 2) {
        return url;
    }

    const shouldUppercaseVehicleSegments = isHitchesOrKitsAreaSlug(segments[1]);
    let reachedVehicleSegments = false;

    const normalizedSegments = segments.map((segment, index) => {
        if (index === 0 || index === 1) {
            return segment.toLowerCase();
        }

        if (!shouldUppercaseVehicleSegments) {
            return segment;
        }

        if (!reachedVehicleSegments) {
            if (isReservedHitchesOrKitsSegment(segment)) {
                return segment;
            }
            reachedVehicleSegments = true;
        }

        return shouldUppercasePathSegment(segment) ? segment.toUpperCase() : segment;
    });

    const normalizedPathname = `/${normalizedSegments.join('/')}`;
    return `${normalizedPathname}${suffix}`;
}
